import {
  formatDate,
  type AuditLogEntry,
  type AuditLogFilters,
  type IconName,
} from '@dataroom/shared';

/**
 * Plain-language activity feed over the room's audit log. Each entry carries what
 * the server recorded and resolved (action, actorEmail, createdAt, and the NAME of
 * the document / checklist item / milestone / folder / person it is about);
 * targetType / targetId / entryHash stay behind the "Details" disclosure. A sentence
 * never claims more than that: an object that is gone reads "(deleted)".
 */
export type ActivityGroup = 'documents' | 'people' | 'agreements' | 'tracking';

export const ACTIVITY_GROUP_LABELS: Record<ActivityGroup, string> = {
  documents: 'Documents & checklist',
  people: 'People & access',
  agreements: 'Agreements & offers',
  tracking: 'Q&A, risks & reports',
};

/** Who an entry with no actor is: an automatic step ClosingVault took itself. */
export const SYSTEM_ACTOR_LABEL = 'ClosingVault (automatic)';

/** action → what the actor did (past tense, after their name). */
const PHRASES: Record<string, string> = {
  // buyer funnel & offers
  'funnel.transition': 'moved a buyer to the next stage',
  'funnel.rollback': 'moved a buyer back a stage',
  'funnel.override_transition': 'moved a buyer to a different stage',
  'funnel.loi_submitted': 'submitted a letter of intent',
  'funnel.loi_accepted': 'accepted a letter of intent',
  'funnel.loi_countered': 'countered a letter of intent',
  'funnel.loi_rejected': 'declined a letter of intent',
  'loi.buyer_signed': 'signed their letter of intent',
  'loi.accept_sign_initiated': 'accepted a letter of intent and started countersigning',
  'loi.redline_initiated': 'started a red-lined counter to a letter of intent',
  // enforced exclusivity (ADR-0046)
  'exclusivity.validation_requested':
    'proposed accepting a letter of intent with exclusivity, pending the owners’ vote',
  // the owners' ownership-weighted vote on what is binding on the company
  'loi.seller_voted_approve_accept': 'voted to approve accepting a letter of intent',
  'loi.seller_voted_decline_accept': 'voted to decline accepting a letter of intent',
  'loi.seller_voted_approve_counter': 'voted to approve countering a letter of intent',
  'loi.seller_voted_decline_counter': 'voted against countering a letter of intent',
  'loi.seller_voted_approve_reject': 'voted to approve declining a letter of intent',
  'loi.seller_voted_decline_reject': 'voted against declining a letter of intent',
  'loi.counter_proposed': 'proposed countering a letter of intent, pending the owners’ vote',
  'loi.reject_proposed': 'proposed declining a letter of intent, pending the owners’ vote',
  'loi.seller_response_declined': 'closed a proposed response to a letter of intent: the owners voted it down',
  'exclusivity.seller_voted_approve_extend': 'voted to approve extending the exclusivity period',
  'exclusivity.seller_voted_decline_extend': 'voted to decline extending the exclusivity period',
  'exclusivity.validated': 'validated the exclusivity for an accepted letter of intent',
  'exclusivity.validation_declined': 'declined to validate an exclusivity',
  'exclusivity.effective': 'started an exclusivity period',
  'exclusivity.buyer_paused': 'paused a buyer’s data room access for the exclusivity period',
  'exclusivity.buyer_restored': 'restored a buyer’s data room access',
  'exclusivity.team_updated': 'changed which of the exclusive buyer’s team keep access',
  'exclusivity.extension_requested': 'asked to extend the exclusivity period',
  'exclusivity.extension_validated': 'approved an extension of the exclusivity period',
  'exclusivity.extension_declined': 'declined an extension of the exclusivity period',
  'exclusivity.extended': 'extended the exclusivity period',
  'exclusivity.ended': 'ended the exclusivity period early',
  'exclusivity.closed': 'ended the exclusivity period at closing',
  'exclusivity.lapsed': 'ended the exclusivity period when it ran out',
  'room.exclusivity_clock_settings_set': 'changed who sees the exclusivity countdown',
  // agreements
  'signing.envelope_created': 'sent an agreement to sign',
  'signing.envelope_cancelled': 'cancelled an agreement',
  'signing.consent_recorded': 'agreed to sign electronically',
  'signing.signed': 'signed an agreement',
  'signing.sealed': 'completed an agreement (signed and sealed)',
  'signing.envelope_sealed': 'completed an agreement (signed and sealed)',
  // access
  'access.grant_created': 'gave someone access',
  'access_grant.create': 'gave someone access',
  'access_grant.create_warnings_acknowledged': 'gave someone access after reviewing the warnings',
  'access.grant_revoked': 'removed someone’s access',
  'access_grant.revoke': 'removed someone’s access',
  'access_grant.auto_revoke': 'ended someone’s access when it expired',
  'access_grant.expiry_updated': 'changed when someone’s access ends',
  'access_grant.invite_defaults': 'invited someone with the default access',
  'access_grant.party_invited': 'invited someone to the room',
  // room
  'room.create': 'created the room',
  'room.renamed': 'renamed the room',
  'room.lifecycle_transition': 'changed the room’s status',
  'room.admin_role_set': 'changed the role they act in for this room',
  'room.admin_transferred': 'handed the room to another admin',
  'room.seller_bound': 'added an owner to the room',
  'room.seller_removed': 'removed an owner from the room',
  'room.seller_ownership_set': 'set the owners’ ownership percentages',
  'room.team_roles_set': 'changed a team member’s roles',
  'room.co_admin_added': 'added a colleague as a room admin',
  'room.co_admin_removed': 'removed a colleague as a room admin',
  'room.trial_expired_trashed': 'moved the room to Trash after the free trial ended',
  'room.trial_restored_on_upgrade': 'brought the room back from Trash when the firm upgraded',
  'room.master_nda_set': 'set the room’s NDA',
  'room.redline_auto_visible_set': 'changed whether buyers see red-lined counters',
  'room.seller_can_respond_loi_set': 'changed whether the seller can respond to offers',
  'room.fiscal_calendar_set': 'changed the fiscal year end',
  'tenant.admin_granted': 'made someone a firm admin',
  'tenant.admin_revoked': 'removed someone as a firm admin',
  'tenant.owner_added': 'made someone an owner of the firm',
  'tenant.owner_removed': 'removed someone as an owner of the firm',
  'tenant.owner_removal_requested': 'asked to remove someone as an owner of the firm',
  'tenant.owner_removal_cancelled': 'cancelled a request to remove an owner',
  'tenant.owner_removal_lapsed': 'let a request to remove an owner lapse',
  'tenant.recovery_contact_requested': 'asked to set the firm’s recovery contact',
  'tenant.recovery_contact_set': 'set the firm’s recovery contact',
  'tenant.recovery_contact_removed': 'removed the firm’s recovery contact',
  // The support-mediated ownership recovery (ADR-0055 §4) — ClosingVault support's acts.
  'tenant.ownership_recovery_requested':
    'asked to assign a new owner (ClosingVault support) — waiting for a second approval',
  'tenant.ownership_recovery_approved':
    'is assigning a new owner (ClosingVault support) — 72 hours for an owner to cancel',
  'tenant.ownership_recovery_cancelled': 'cancelled an ownership recovery',
  'tenant.ownership_recovery_completed': 'completed an ownership recovery',
  'tenant.ownership_recovery_expired': 'let an ownership recovery expire',
  'actor.reenabled': 'switched a disabled account back on (an invitation)',
  'tenant.ownership_recovery_reversal_requested':
    'asked to reverse an ownership recovery (ClosingVault support) — a second staff member confirms',
  'tenant.ownership_recovery_reversed': 'reversed an ownership recovery (ClosingVault support)',
  'tenant.classification_learning_opted_out':
    'stopped sharing anonymized filing patterns with all customers',
  'tenant.classification_learning_opted_in':
    'started sharing anonymized filing patterns with all customers',
  // documents & AI filing
  'document.uploaded': 'uploaded a document',
  'document.read': 'viewed a document',
  'document.download': 'downloaded a document',
  'document.bulk_read_throttled': 'was paused for opening documents too quickly',
  'document.auto_filed': 'filed a document',
  'document.filed_by_precedent': 'filed a document like the rest of its series',
  'document.placeholder_choice_applied':
    'filed a document where a person pointed it, so its account can be made from it',
  'document.account_misfile_removed':
    'took a document out of an account that belongs to another institution',
  'document.placeholder_filing_removed':
    'took a document off a placeholder folder so it can be filed under its account',
  'document.attributes_modified': 'changed a document’s folders',
  'document.reclassify_requested': 'asked the AI to re-file a document',
  'documents.reclassify_unfiled_requested': 're-ran the AI’s filing on the room’s unfiled documents',
  'documents.reclassify_bulk_requested': 'asked the AI to re-file the documents that needed a look',
  'document.unfiled_loi_private': 'kept a letter of intent out of the folders',
  'document.version_merged': 'kept a newer upload as the current version of a document',
  'document.version_split': 'split a version back out into its own document',
  'document.version_suggested': 'flagged an upload as a possible new version of a document',
  'document.version_kept_separate': 'kept a possible new version as its own document',
  'document.version_suggestion_dismissed':
    'withdrew a possible new version: the two are different documents (another form, type, period or account)',
  'document.version_read': 'opened a version from a document’s history',
  'document.upload_skipped_identical': 'uploaded a file already in the room, so it was skipped',
  'document.unpacked': 'unpacked a zip or email into its files',
  'document.readable_copy_uploaded': 'uploaded a readable copy of a file we couldn’t read',
  'document.unreadable_cleared':
    'cleared a “couldn’t read” flag: the file reads fine, so the seller’s request was withdrawn',
  'document.current_copy_uploaded': 'uploaded a current copy of a contract',
  'document.visibility_override_set': 'changed who can see a document',
  'document.dating_set': 'set a document’s period or date of data',
  'document.statement_basis_set_accrual': 'said a financial statement is on the accrual basis',
  'document.statement_basis_set_cash': 'said a financial statement is on the cash basis',
  'document.statement_basis_set_modified_cash':
    'said a financial statement is on the modified cash (tax) basis',
  'document.statement_basis_set_not_stated': 'said a financial statement doesn’t state its basis',
  'tag.hierarchy_imported': 'imported the room’s folders',
  'tag.audience_changed': 'changed who gets a folder by default',
  'tag.audience_changed_warnings_acknowledged':
    'changed who gets a folder by default after reviewing the warnings',
  'proposal.approve': 'accepted the AI’s filing suggestion',
  'proposal.reject': 'rejected the AI’s filing suggestion',
  // checklist
  'dd.template_instantiated': 'set up the checklist',
  'dd.requests_imported': 'imported checklist items',
  'dd.request_created': 'added a checklist item',
  'dd.request_deleted': 'removed a checklist item',
  'dd.request_updated': 'edited a checklist item',
  'dd.request_required_set': 'changed whether a checklist item is required',
  'dd.request_na_attested': 'confirmed a checklist item doesn’t apply to the company',
  'dd.request_na_set': 'marked a checklist item as not applicable',
  'dd.request_na_cleared': 'said a checklist item applies after all',
  'dd.request_completed': 'marked a checklist item complete',
  'dd.request_reopened': 'reopened a checklist item',
  'dd.document_linked': 'provided a document for a checklist item',
  'dd.document_unlinked': 'took a document off a checklist item',
  'dd.document_auto_linked': 'matched a document to a checklist item',
  'dd.document_auto_unlinked': 'took back an automatic checklist match',
  'dd.phase_created': 'added a checklist phase',
  'dd.phase_released': 'released a checklist phase to the seller',
  'dd.phase_release_date_set': 'scheduled when a checklist phase is released',
  'dd.phase_dates_set': 'set the start and due dates of a checklist phase',
  'dd.phase_deleted': 'deleted a checklist phase',
  'dd.phase_renamed': 'renamed a checklist phase',
  'dd.question_created': 'asked the seller a question',
  'dd.question_updated': 'edited a question for the seller',
  'dd.question_deleted': 'removed a question for the seller',
  'dd.question_answered': 'answered a checklist question',
  'dd.checklist_template_saved': 'saved a checklist for reuse',
  'dd.checklist_template_deleted': 'deleted a saved checklist',
  // the seller's section certifications (two-tick: Classified + Certified)
  'section.certified_complete': 'certified a checklist section complete',
  'section.certified_not_applicable': 'certified a checklist section doesn’t apply to the company',
  'section.certification_withdrawn': 'withdrew a section certification',
  'section.certification_invalidated':
    'changed a certified section, so the seller needs to certify it again',
  // the company's accounts (ADR-0037; numbers never appear here)
  'dd.account_created': 'added a company account to the checklist',
  'dd.account_updated': 'updated a company account',
  'dd.accounts_merged': 'merged two company accounts into one',
  'dd.account_merged_away': 'merged a company account into another',
  'dd.account_pruned': 'removed an empty company account the classifier had added',
  'dd.account_level_folded': 'moved documents from a duplicate folder into their company account',
  'dd.account_tree_restructured': 'grouped the company accounts under their banks',
  'dd.account_number_set': 'stored an account number',
  'dd.account_number_cleared': 'removed a stored account number',
  'dd.account_number_revealed': 'viewed a stored account number',
  'dd.state_added': 'added a state to the checklist',
  'dd.state_removed': 'removed a state from the checklist',
  // sign-in & accounts
  'auth.register': 'created their account',
  'auth.invitation_sent': 'sent an invitation',
  'auth.room_access_notice_sent': 'let them know they were added to the room',
  'auth.email_code_sent': 'was sent a sign-in code',
  'auth.email_code_issued_by_operator': 'was issued a sign-in code by support',
  'auth.email_code_sign_in': 'signed in with an emailed code',
  'auth.email_verified': 'confirmed their email address',
  'auth.login': 'signed in',
  'auth.logout': 'signed out',
  'auth.login_failed': 'failed a sign-in attempt',
  'auth.login_blocked_locked': 'was blocked from signing in while locked out',
  'auth.login_blocked_unverified': 'was blocked from signing in until their email is confirmed',
  'auth.login_mfa_pending': 'started signing in (authenticator code next)',
  'auth.login_mfa_enrollment_required': 'started signing in (authenticator setup required)',
  'auth.lockout': 'was locked out after repeated failed sign-ins',
  'auth.mfa_enroll_started': 'started setting up an authenticator',
  'auth.mfa_enroll_confirmed': 'set up an authenticator',
  'auth.mfa_verified': 'entered a valid authenticator code',
  'auth.mfa_failed': 'entered a wrong authenticator code',
  'auth.mfa_lockout': 'was locked out after repeated wrong authenticator codes',
  'auth.password_set': 'set their password',
  'auth.unverified_account_reset': 'had an unconfirmed account reset',
  // buyer Q&A (ADR-0039)
  'buyer_question.asked': 'asked a question',
  'buyer_question.routed_to_seller': 'sent a buyer question to the seller',
  'buyer_question.answered': 'answered a buyer question',
  'buyer_question.answer_drafted': 'drafted an answer to a buyer question',
  'buyer_question.answer_released': 'released an answer to a buyer',
  'buyer_question.closed': 'closed a buyer question',
  'buyer_question.reopened': 'reopened a buyer question',
  'buyer_question.published': 'shared an answered question with all buyers',
  'buyer_question.published_edited': 'edited a question shared with all buyers',
  'buyer_question.unpublished': 'stopped sharing a question with all buyers',
  // buyer teams
  'buyer_team.request_created': 'asked to add someone to their buying team',
  'buyer_team.request_approved': 'approved adding someone to a buyer’s team',
  'buyer_team.request_declined': 'declined adding someone to a buyer’s team',
  'buyer_team.request_withdrawn': 'withdrew a request to add someone to their team',
  'buyer_team.member_added': 'added someone to a buyer’s team',
  'buyer_team.member_removed': 'removed someone from a buyer’s team',
  // notifications
  'alerts.activity_settings_updated': 'changed their buyer-activity alert settings',
  'alerts.activity_sent': 'sent a buyer-activity alert',
  'alerts.new_documents_opted_in': 'turned on new-document alerts',
  'alerts.new_documents_opted_out': 'turned off new-document alerts',
  'alerts.new_documents_sent': 'sent a new-documents alert',
  // redaction
  'redaction.mode_auto': 'turned automatic redaction on for the room',
  'redaction.mode_off': 'turned automatic redaction off for the room',
  'redaction.detect_requested': 'asked for a document to be checked for sensitive details',
  'redaction.apply_requested': 'asked for the redactions on a document to be applied',
  'redaction.approved': 'approved the redacted copy of a document',
  'redaction.mark_approved': 'confirmed a detail should be redacted',
  'redaction.mark_rejected': 'kept a detail visible (not redacted)',
  'redaction.manual_box_added': 'covered an area of a document by hand',
  'redaction.rendition_read': 'opened the redacted copy of a document',
  'redaction.detected': 'checked a document for sensitive details',
  'redaction.applied': 'made the redacted copy of a document',
  'redaction.ready': 'finished the redacted copy of a document',
  'redaction.not_needed': 'found nothing to redact in a document',
  'redaction.unsupported': 'could not redact this kind of file',
  'redaction.needs_review': 'asked for a person to look at a document’s redaction',
  'redaction.auto_retry': 'retried a redaction that did not finish',
  'redaction.failed': 'could not finish a document’s redaction',
  // platform staff (never in a room's feed)
  'affiliate.suspend': 'suspended an affiliate',
  'affiliate.reinstate': 'reinstated an affiliate',
  'affiliate_payout.mark_paid': 'marked an affiliate payout as paid',
  // risks & issues, the deal timeline, and the weekly status email (ADR-0047)
  'risk.created': 'logged a risk or issue',
  // the contract register (ADR-0054)
  'contract_terms.corrected': 'corrected a contract’s dates',
  'contract_terms.rescan_requested': 'asked the AI to re-read the room’s contracts',
  'risk.updated': 'updated a risk or issue',
  'risk.status_changed': 'changed the status of a risk or issue',
  'milestone.created': 'added a milestone to the deal timeline',
  'milestone.updated': 'updated a milestone on the deal timeline',
  'milestone.deleted': 'removed a milestone from the deal timeline',
  'digest.settings_updated': 'changed the weekly status email settings',
  'digest.unsubscribed': 'stopped receiving the weekly status email',
  'digest.resubscribed': 'started receiving the weekly status email again',
  'digest.sent': 'sent the weekly status email',
  'digest.preview_sent': 'sent a preview of the weekly status email',
};

const GROUP_BY_PREFIX: [string, ActivityGroup][] = [
  ['funnel.', 'agreements'],
  ['loi.', 'agreements'],
  ['signing.', 'agreements'],
  // Exclusivity follows an accepted offer — before the general `room.` rule.
  ['exclusivity.', 'agreements'],
  ['room.exclusivity_', 'agreements'],
  ['buyer_question.', 'tracking'],
  ['risk.', 'tracking'],
  ['milestone.', 'tracking'],
  ['digest.', 'tracking'],
  ['access', 'people'],
  ['room.', 'people'],
  ['tenant.', 'people'],
  ['auth.', 'people'],
];

export function activityGroup(action: string): ActivityGroup {
  return GROUP_BY_PREFIX.find(([p]) => action.startsWith(p))?.[1] ?? 'documents';
}

const ICON: Record<ActivityGroup, IconName> = {
  documents: 'file',
  people: 'users',
  agreements: 'pen',
  tracking: 'activity',
};

/** What the actor did, in words. An action we have no wording for yet still
 * reads as a sentence ("recorded: Dd question reordered"), never a raw code. */
export function activityPhrase(action: string, targetName?: string): string {
  if (action === 'tag.hierarchy_imported' && targetName) return `added the folder “${targetName}”`;
  const known = PHRASES[action];
  if (known) return known;
  const words = action.replace(/[._]+/g, ' ').trim();
  return `recorded: ${words.charAt(0).toUpperCase()}${words.slice(1)}`;
}

/** One named thing an entry is about: "Document", "report.pdf". */
export interface ActivityObjectLine {
  label: string;
  /** Null when the object is gone ("(deleted)") or never had a name. */
  name: string | null;
  deleted: boolean;
}

/** What each audit target type is called to a person. */
const TARGET_LABELS: Record<string, string> = {
  document: 'Document',
  document_version: 'Document',
  signing_envelope: 'Agreement',
  document_tag_proposal: 'Document',
  redaction_run: 'Document',
  dd_request: 'Checklist item',
  dd_phase: 'Checklist phase',
  dd_phase_question: 'Checklist question',
  dd_account: 'Account',
  dd_room_state: 'State',
  section_certification: 'Checklist section',
  room_milestone: 'Milestone',
  room_risk: 'Risk or issue',
  tag: 'Folder',
  buyer_question: 'Question',
  actor: 'Person',
  access_grant: 'Person',
  buyer_team_request: 'Person',
  buyer_funnel_state: 'Buyer',
  funnel_stage_override: 'Buyer',
  loi_submission: 'Buyer',
  room_exclusivity: 'Buyer',
};
const OBJECT_TYPE_LABELS: Record<string, string> = {
  document: 'Document',
  item: 'Checklist item',
  milestone: 'Milestone',
  folder: 'Folder',
  person: 'Person',
  risk: 'Risk or issue',
  question: 'Question',
};

/** The named objects of an entry, in reading order: the checklist item, then the
 * document that was provided for it. */
export function activityObjects(entry: AuditLogEntry): ActivityObjectLine[] {
  const lines: ActivityObjectLine[] = [];
  if (entry.object) {
    lines.push({
      label: TARGET_LABELS[entry.targetType] ?? OBJECT_TYPE_LABELS[entry.object.type] ?? 'Item',
      name: entry.object.name,
      deleted: entry.object.deleted,
    });
  }
  if (entry.related) {
    lines.push({ label: 'Document', name: entry.related.name, deleted: false });
  }
  return lines;
}

export interface ActivityItem {
  entry: AuditLogEntry;
  /** The document / checklist item / milestone / folder / person it is about, by name. */
  objects: ActivityObjectLine[];
  /** Who did it — the actor's email, or null for an automatic (system) step. */
  actor: string | null;
  phrase: string;
  group: ActivityGroup;
  icon: IconName;
}

export interface ActivityDay {
  /** "Today · Oct 2, 2026", "Yesterday · Oct 1, 2026", or "Sep 28, 2026". */
  label: string;
  items: ActivityItem[];
}

function localDayKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** Entries (newest first, as served) grouped into local calendar days. */
export function groupActivityByDay(
  entries: AuditLogEntry[],
  now: Date = new Date(),
): ActivityDay[] {
  const today = localDayKey(now);
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  const yesterday = localDayKey(y);
  const days: ActivityDay[] = [];
  const byKey = new Map<string, ActivityDay>();
  for (const entry of entries) {
    const at = new Date(entry.createdAt);
    const key = localDayKey(at);
    let day = byKey.get(key);
    if (!day) {
      const date = formatDate(at);
      const label =
        key === today ? `Today · ${date}` : key === yesterday ? `Yesterday · ${date}` : date;
      day = { label, items: [] };
      byKey.set(key, day);
      days.push(day);
    }
    const group = activityGroup(entry.action);
    day.items.push({
      entry,
      objects: activityObjects(entry),
      actor: entry.actorEmail,
      phrase: activityPhrase(
        entry.action,
        entry.object?.type === 'folder' ? (entry.object.name ?? undefined) : undefined,
      ),
      group,
      icon: ICON[group],
    });
  }
  return days;
}

// ---------------------------------------------------------------------------
// Search: what the person typed, and the server-side filters it becomes.

/** Where the typed text is looked for. */
export type SearchField =
  | 'any'
  | 'person'
  | 'action'
  | 'document'
  | 'item'
  | 'milestone'
  | 'folder';

export const SEARCH_FIELD_LABELS: Record<SearchField, string> = {
  any: 'Everything',
  person: 'Person',
  action: 'What happened',
  document: 'Document',
  item: 'Checklist item',
  milestone: 'Milestone',
  folder: 'Folder',
};

export interface ActivitySearch {
  text: string;
  field: SearchField;
  /** Calendar days (YYYY-MM-DD, local), both inclusive; '' = unbounded. */
  from: string;
  to: string;
  /** Show views and system activity too. */
  showAll: boolean;
}

export const EMPTY_ACTIVITY_SEARCH: ActivitySearch = {
  text: '',
  field: 'any',
  from: '',
  to: '',
  showAll: false,
};

/** Something other than the default feed is being asked for (search text or dates). */
export function isSearching(search: ActivitySearch): boolean {
  return search.text.trim() !== '' || search.from !== '' || search.to !== '';
}

/** Every action whose wording (or code) holds ALL the words typed. The server only
 * knows codes; the wording lives here. */
export function actionsMatching(text: string): string[] {
  const words = text.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  return Object.entries(PHRASES)
    .filter(([action, phrase]) => {
      const hay = `${phrase} ${action.replace(/[._]+/g, ' ')}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    })
    .map(([action]) => action);
}

/** An action filter that matches no action: sent when typed wording matches no
 * entry's wording, so the answer is an honest "nothing" (an empty list would
 * mean "no filter"). */
const NO_ACTION = 'none.none';

function dayStart(ymd: string, plusDays = 0): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + plusDays).toISOString();
}

/** The server-side filters for what is typed: dates are the person's local days. */
export function activitySearchFilters(search: ActivitySearch): AuditLogFilters {
  const filters: AuditLogFilters = {};
  if (search.showAll) filters.includeAll = true;
  const text = search.text.trim();
  if (text) {
    if (search.field === 'any') {
      filters.q = text;
      const actions = actionsMatching(text);
      if (actions.length > 0) filters.qActions = actions;
    } else if (search.field === 'action') {
      const actions = actionsMatching(text);
      filters.actions = actions.length > 0 ? actions : [NO_ACTION];
    } else {
      filters[search.field] = text;
    }
  }
  const from = dayStart(search.from);
  if (from) filters.from = from;
  const to = dayStart(search.to, 1); // the "to" day is included: stop at the next midnight
  if (to) filters.to = to;
  return filters;
}
